import React from 'react';
import { Button, message, notification, Col } from 'antd';
import dayjs from 'dayjs';
import { complement, compose, find, isNil } from 'ramda';
import {
  CopyOutlined,
  ExclamationCircleFilled,
  CheckCircleFilled,
} from '@ant-design/icons';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import {
  ImportExportStatusCode,
  Job,
  JobState,
  jobWaitingStates,
} from './types';

export const renderValidationMark = (valid: boolean) =>
  valid ? (
    <CheckCircleFilled style={{ fontSize: 14, color: '#54b25c' }} />
  ) : (
    <ExclamationCircleFilled style={{ fontSize: 14, color: '#faad14' }} />
  );

export const renderValidationMarkCol = (
  valid: boolean,
  marginTop: number = 0
) => (
  <Col span={2} style={{ marginTop }}>
    {renderValidationMark(valid)}
  </Col>
);

export function getBase64(file: Blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
}

export const hasJobInProgress = compose<Job[], Job | undefined, boolean>(
  complement(isNil),
  find((j) => jobWaitingStates.includes(j.state))
);

export function assertNever(x: never): never {
  throw new Error(`Unexpected object: ${x}`);
}

export const convertImportExportStatusCodeToJobStatus = (
  code: ImportExportStatusCode
) => {
  switch (code) {
    case 0:
    case 13:
      return JobState.Completed;
    case 1:
      return JobState.Created;
    case 100:
      return JobState.Queued;
    case 2:
    case 14:
      return JobState.Reverting;
    case 16:
      return JobState.RevertError;
    case 10:
    case 11:
    case 12:
    case 500:
      return JobState.Failed;
    default:
      return assertNever(code);
  }
};

export const convertJobStatusToImportExportStatusCode = (
  status: JobState
): ImportExportStatusCode => {
  switch (status) {
    case JobState.Queued:
      return 100;
    case JobState.InProgress:
      return 2;
    case JobState.Created:
      return 1;
    case JobState.Completed:
      return 0;
    case JobState.Failed:
      return 12;
    case JobState.Reverted:
      return 13;
    case JobState.Reverting:
      return 14;
    case JobState.RevertError:
      return 16;
    default:
      return assertNever(status);
  }
};

export function formatCreationDate(
  inputDate: string
): string | React.ReactElement {
  const formattedDate = dayjs(inputDate).format('DD.MM.YYYY HH:mm').split(' ');
  const [date, time] = formattedDate;
  return (
    <>
      <div>
        {date} {time}
      </div>
    </>
  );
}

type CopyErrorButtonProps = {
  text: string;
  msg: string;
};

const CopyErrorButton: React.FC<CopyErrorButtonProps> = ({ text, msg }) => (
  //@ts-ignore
  <CopyToClipboard text={text}>
    <Button
      shape="circle"
      icon={<CopyOutlined />}
      onClick={() => message.info(msg)}
    />
  </CopyToClipboard>
);

interface ClipboardMessage {
  [key: string]: string;
  en: string;
  es: string;
  de: string;
}

const clipboardMessage: ClipboardMessage = {
  en: 'Copied to clipboard',
  es: 'Copiado a portapapeles',
  de: 'In die Zwischenablage kopiert',
};
// eslint-disable-next-line import/prefer-default-export
export const handleErrorNotification = ({
  error,
  lang,
  placement = 'topRight',
}: {
  error: any;
  lang: string;
  placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
}) => {
  let errorDescription;

  // Handle localized error messages
  if (error && typeof error === 'object' && (error[lang] || error.en)) {
    errorDescription = error[lang] || error.en;
  } else if (error.description === 'Serialization error') {
    if (Array.isArray(error.fields)) {
      errorDescription = error.fields.join('\n');
    } else {
      const fields = error.fields || {};
      if (Object.keys(fields).length !== 0) {
        errorDescription = JSON.stringify(fields);
      } else {
        errorDescription = JSON.stringify(error);
      }
    }
  } else {
    const fields = error.fields || {};
    if (Object.keys(fields).length !== 0) {
      errorDescription = JSON.stringify(fields);
    } else {
      errorDescription = JSON.stringify(error);
    }
  }

  notification.error({
    message: 'An Error occurred',
    description: errorDescription,
    placement,
    btn: (
      <CopyErrorButton
        text={errorDescription}
        msg={lang ? clipboardMessage[lang.toLowerCase()] : clipboardMessage.de}
      />
    ),
  });
};
